import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Download } from 'lucide-react';
import { isWebAppReady } from '../config/systemConfig';
import { SEO } from '../components/SEO';

export const Home = () => {
  const steps = [
    {
      title: "Upload Image",
      description: "Start by uploading your favorite photo. Our platform accepts high-quality images that will serve as the foundation for your personalized artwork.",
      image: "/assets/process/leftUp.jpg"
    },
    {
      title: "Select Style",
      description: (
        <span>
          Browse through our curated collection of <Link to="/styles" className="text-black underline hover:text-gray-600">artistic styles</Link> and choose the one that best matches your vision. Each style transforms your image into a unique masterpiece.
        </span>
      ),
      image: "/assets/bibliotheque/ClaudeMonet/Claude_Monet-Waterlilies.jpg"
    },
    {
      title: "Get Paint",
      description: "Watch as our AI transforms your selected image into a stunning painted artwork. The process combines your photo with the chosen artistic style to create a one-of-a-kind masterpiece.",
      image: "/assets/process/rightUp.png"
    },
    {
      title: "Customize Products",
      description: "Select from a variety of premium products like canvas prints, framed art, or home decor items. Customize sizes, frames, and finishes to match your space perfectly.",
      image: "/assets/process/leftBottom.jpeg"
    },
    {
      title: "Place Order",
      description: "Review your customized creation and place your order with confidence. We handle printing, quality control, and shipping to bring your artwork to life.",
      image: "/assets/process/rightBottom.png"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Atelier Persopol - Transform Your Photos into Art"
        description="Transform your favorite photos into stunning painted masterpieces with Atelier Persopol. Create unique custom canvas prints, framed art, and home decor from your memories."
      />
      {/* Hero Section */}
      <section className="pt-12 md:pt-20 pb-12 md:pb-16 text-center container mx-auto px-4">
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-4 md:mb-6 leading-tight"
        >
          <span className="text-gray-400">Where Your</span> <span className="font-bold">Moments</span> <span className="text-gray-400">Become</span> <span className="font-bold">Living Design</span>
        </motion.h1>
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto"
        >
          Atelier Persopol transforms your personal photos into bespoke artistic masterpieces. Using advanced AI, we reimagine your memories as classic paintings, ready to be printed on premium canvas and framed for your home.
        </motion.p>

        {/* Combined Result */}
        <div className="max-w-4xl mx-auto mt-8 md:mt-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl md:rounded-3xl overflow-hidden shadow-xl md:shadow-2xl"
          >
            <img src="/assets/process/combined.jpg" alt="Final Result" className="w-full h-auto" />
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="container mx-auto px-4 py-8 md:py-12">
        {/* Process Intro Header */}
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-serif font-bold mb-4"
          >
            How It Works
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-gray-600 max-w-2xl mx-auto"
          >
            Create your own masterpiece in just a few simple steps. From uploading your photo to receiving a museum-quality print.
          </motion.p>
        </div>

        {/* Steps List */}
        <div className="max-w-5xl mx-auto mb-12 md:mb-24 space-y-12 md:space-y-20">
          {steps.map((step, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-8 md:gap-16`}
            >
              {/* Image Side */}
              <div className="flex-1 w-full">
                <div className="aspect-square rounded-2xl overflow-hidden shadow-lg bg-gray-100">
                  <img 
                    src={step.image} 
                    alt={step.title} 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                  />
                </div>
              </div>

              {/* Text Side */}
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold mb-3 md:mb-4">Step {index + 1}. {step.title}</h3>
                <p className="text-base md:text-lg text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Button */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row justify-center items-center gap-4 pb-20"
        >
          {isWebAppReady ? (
            <Link 
              to="/signin" 
              className="inline-flex items-center space-x-2 md:space-x-3 bg-black text-white text-base md:text-lg font-medium px-8 md:px-10 py-4 md:py-5 rounded-full hover:bg-gray-800 transition-all transform hover:scale-105 shadow-xl touch-manipulation"
            >
              <span>Get Started</span>
              <ArrowRight className="w-5 h-5 md:w-6 md:h-6" />
            </Link>
          ) : (
            <button 
              className="inline-flex items-center space-x-2 md:space-x-3 bg-gray-400 text-white text-base md:text-lg font-medium px-8 md:px-10 py-4 md:py-5 rounded-full cursor-not-allowed shadow-xl touch-manipulation"
              disabled
            >
              <span>Coming Soon</span>
            </button>
          )}

          <a 
            href="https://apps.apple.com/jp/app/atelier-persopol/id6752743785?l=en-US"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 md:space-x-3 bg-white text-black border-2 border-black text-base md:text-lg font-medium px-8 md:px-10 py-4 md:py-5 rounded-full hover:bg-gray-50 transition-all transform hover:scale-105 shadow-xl touch-manipulation"
          >
            <span>iOS App</span>
            <Download className="w-5 h-5 md:w-6 md:h-6" />
          </a>
        </motion.div>
      </section>
    </div>
  );
};
